type TimelineViewProps = {
    shippingData: any[];
    dateColumns: string[];
    fullDate: boolean;
};

function getTodayDate() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getDateOnly(dateTimeString: string): string {
    if (dateTimeString) {
        return dateTimeString.split(" ")[0];
    } else {
        return "";
    }
}

function isDateOlderThanToday(dateString: string): boolean {
    const inputDate = new Date(dateString);
    const today = new Date();

    inputDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return inputDate < today;
}

function isDate1BeforeDate2(date1: string, date2: string): boolean {
    return new Date(date1) < new Date(date2);
}

const ETAChip = (row: any, date: string) => {
    const hasDatePlanHistoryAndDateIncludesInHistory =
        row.date_planned_history?.length > 0 &&
        row.date_planned_history.includes(date);

    const isTodayDate = getDateOnly(row.date_planned) == date;

    /**
     * To prevent stacking with shipped date and
     * ship arrived date
     */
    const isNotShippedDateAndNotShipArrivedDate =
        getDateOnly(row.effective_date) !== getDateOnly(row.date_planned) &&
        getDateOnly(row.shipped_date) !== getDateOnly(row.date_planned);

    if (
        (hasDatePlanHistoryAndDateIncludesInHistory || isTodayDate) &&
        isNotShippedDateAndNotShipArrivedDate
    ) {
        return (
            <span
                className="badge"
                style={{
                    backgroundColor: "#22cc00ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                }}
            >
                ETA
            </span>
        );
    }
};

const LateChip = (row: any, date: string) => {
    // Guard clause: must not have effective_date
    if (row.effective_date) return null;

    // Guard clause: date must be older than today
    if (!isDateOlderThanToday(date)) return null;

    // Guard clause: date must not match planned or shipped date
    if (getDateOnly(row.date_planned) === date) return null;
    if (getDateOnly(row.shipped_date) === date) return null;

    // Guard clause: if shipped_date exists, date must be before it
    if (row.shipped_date && !isDate1BeforeDate2(date, row.shipped_date)) {
        return null;
    }

    // Check if date falls within the date_planned_history range
    const isWithinHistoryRange =
        row.date_planned_history?.length > 0 &&
        isDate1BeforeDate2(
            date,
            row.date_planned_history[row.date_planned_history.length - 1],
        ) &&
        isDate1BeforeDate2(row.date_planned_history[0], date);

    // Check if planned date is in the past and date is not before it
    const isPlannedDateOverdue =
        !isDate1BeforeDate2(date, row.date_planned) &&
        isDateOlderThanToday(row.date_planned);

    if (isWithinHistoryRange || isPlannedDateOverdue) {
        return (
            <span
                className="badge"
                style={{
                    backgroundColor: "#f57242ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                }}
            >
                Late
            </span>
        );
    }

    return null;
};

const WaitingChip = (row: any, date: string) => {
    const shipArrivedAndShippedAndIsDateBeforeShipped =
        row.effective_date &&
        row.shipped_date &&
        isDate1BeforeDate2(date, row.effective_date);

    const shippedButBeforeToday =
        row.shipped_date &&
        isDateOlderThanToday(row.shipped_date) &&
        isDateOlderThanToday(date) &&
        isDate1BeforeDate2(row.shipped_date, date);

    /**
     * To prevent stacking with ship arrived date and shipped ate
     */
    const isNotShippedDateAndNotShipArrivedDate =
        date !== getDateOnly(row.effective_date) &&
        date !== getDateOnly(row.shipped_date) &&
        date !== getDateOnly(row.date_planned);
    if (
        shipArrivedAndShippedAndIsDateBeforeShipped ||
        (shippedButBeforeToday && isNotShippedDateAndNotShipArrivedDate)
    ) {
        return (
            <span
                className="badge"
                style={{
                    backgroundColor: "rgba(253, 249, 0, 1)",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                }}
            >
                {row.is_dropship ? "Wait For Delivery" : "Clearance"}
            </span>
        );
    }
};

const ShipArrivedChip = (row: any, date: string) => {
    const hasInspectionDateAndItsToday =
        row.dropship_inspection_date &&
        getDateOnly(row.dropship_inspection_date) == date;

    const isShipArrivedAndIsToday =
        row.shipped_date && getDateOnly(row.shipped_date) == date;

    const shipArrivedDateIsNotShippedDate =
        getDateOnly(row.shipped_date) !== getDateOnly(row.effective_date);

    if (isShipArrivedAndIsToday && shipArrivedDateIsNotShippedDate) {
        return (
            <span
                className="badge"
                style={{
                    backgroundColor: "#22cc00ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                    position: "relative",
                    zIndex: 2,
                }}
            >
                Ship Arrived
                {hasInspectionDateAndItsToday && " | Inspected"}
            </span>
        );
    }
};

const DeliveryChip = (row: any, date: string) => {
    if (row.is_dropship) {
        const hasShippedDateAndIsToday =
            row.dropship_delivery_date &&
            getDateOnly(row.dropship_delivery_date) == date;

        const hasInspectionDateAndItsToday =
            row.dropship_inspection_date &&
            getDateOnly(row.dropship_inspection_date) == date;

        // if (hasShippedDateAndIsToday) {
        //     return (
        //         <span
        //             className="badge"
        //             style={{
        //                 backgroundColor: "#22cc00ff",
        //                 fontWeight: "700",
        //                 fontSize: "0.7rem",
        //                 color: "#333",
        //                 display: "inline",
        //             }}
        //         >
        //             Dropship Delivery{" "}
        //             {hasInspectionDateAndItsToday && "| Inspected"}
        //         </span>
        //     );
        // }
    } else {
        const hasShippedDateAndIsToday =
            row.effective_date && getDateOnly(row.effective_date) == date;

        if (hasShippedDateAndIsToday) {
            return (
                <span
                    className="badge"
                    style={{
                        backgroundColor: "#22cc00ff",
                        fontWeight: "700",
                        fontSize: "0.7rem",
                        color: "#333",
                        display: "inline",
                        zIndex: 2,
                    }}
                >
                    Receipt
                </span>
            );
        }
    }
};

const EarlyChip = (row: any, date: string) => {
    const isShipArrivedAndDateIsBetweenShipArrivedAndETA =
        row.shipped_date &&
        isDate1BeforeDate2(row.shipped_date, date) &&
        isDate1BeforeDate2(date, row.date_planned);

    /**
     * To prevent stacking
     */
    const shouldShow =
        getDateOnly(row.effective_date) !== date &&
        getDateOnly(row.date_planned) !== date &&
        getDateOnly(row.shipped_date);
    if (isShipArrivedAndDateIsBetweenShipArrivedAndETA && shouldShow) {
        return (
            <span
                className="badge"
                style={{
                    backgroundColor: "#5fbbe6ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                }}
            >
                Early
            </span>
        );
    }
};

const OrgBlChip = (row: any, date: string) => {
    const hasOrgBlDateAndItsToday =
        row.original_bl_date && getDateOnly(row.original_bl_date) == date;

    if (hasOrgBlDateAndItsToday) {
        return (
            <span
                className="badge"
                style={{
                    backgroundColor: "#22cc00ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                    zIndex: 2,
                }}
            >
                Release
            </span>
        );
    }
};

const InspectionChip = (row: any, date: string) => {
    const hasInspectionDateAndItsToday =
        row.dropship_inspection_date &&
        getDateOnly(row.dropship_inspection_date) == date;

    const shouldShow =
        row.is_dropship &&
        getDateOnly(row.dropship_inspection_date) !==
            getDateOnly(row.shipped_date);
    //     &&
    // getDateOnly(row.dropship_inspection_date) !==
    //     getDateOnly(row.dropship_delivery_date);

    if (hasInspectionDateAndItsToday && shouldShow) {
        return (
            <span
                className="badge"
                style={{
                    backgroundColor: "#22cc00ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                }}
            >
                Inspected
            </span>
        );
    }
};

const TimelineView = (props: TimelineViewProps) => {
    return (
        <table
            className="table table-hover mb-0"
            style={{
                borderRight: "1px solid #efefef",
                borderLeft: "1px solid #efefef",
            }}
        >
            <thead
                style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#0d6efd",
                    color: "white",
                    zIndex: 10,
                }}
            >
                <tr>
                    {props.dateColumns.map((date: string, idx) => (
                        <th
                            key={idx}
                            className="fw-semibold text-uppercase text-center"
                            style={{
                                fontSize: "0.7rem",
                                color: "#6c757d",
                                padding: "0.6rem",
                                borderBottom: "2px solid #dee2e6",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {props.fullDate ? date : date.split("-")[2]}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {props.shippingData.map((row: any, idx) => (
                    <tr
                        key={idx}
                        style={{
                            borderBottom: "1px solid #f0f0f0",
                            height: 50,
                        }}
                    >
                        {props.dateColumns.map((date, dateIdx) => (
                            <td
                                key={dateIdx}
                                className="text-center"
                                style={{
                                    padding: "0.5rem",
                                    maxWidth: 10,
                                    backgroundColor:
                                        getTodayDate() == date
                                            ? "#6bb6deff"
                                            : "#fff",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        overflow: "visible",
                                    }}
                                >
                                    {ETAChip(row, date)}
                                    {LateChip(row, date)}
                                    {WaitingChip(row, date)}
                                    {ShipArrivedChip(row, date)}
                                    {DeliveryChip(row, date)}
                                    {EarlyChip(row, date)}
                                    {InspectionChip(row, date)}
                                    {OrgBlChip(row, date)}
                                </div>
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
            <style>{`
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  
  .scrollbar-hide {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  
  .table-hover tbody tr:hover {
    background-color: #f8f9fa;
  }
`}</style>
        </table>
    );
};

export default TimelineView;
